import store from 'redux/store';
import { toast } from 'react-toastify';
import { confirmDialog } from 'dialogs';
import { clearEventInstance, clearAllEventInstances } from 'redux/slices/eventsSlice';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';

type EventQueueEntity = Extract<ActionEntity, { type: 'event_queue' }>;

const isAllEventQueue = (entities: readonly ActionEntity[]): entities is readonly EventQueueEntity[] =>
  entities.every((entity) => entity.type === 'event_queue');

/**
 * Queue-level actions for an event's raised event_instance queue. The backend
 * consumes raised instances FIFO and the compatibility clear endpoint always
 * clears the *oldest* raised instance regardless of which id is supplied, so a
 * per-row Clear was misleading; these operate on the whole raised queue instead.
 *
 * `clearNextEventInstance` clears the oldest raised instance (the one a consumer
 * would pick up next); `clearAllEventInstances` drains the entire raised queue
 * after a confirm. Both target a single event and are disabled when nothing is
 * raised — they gate on the queue metadata carried on the entity (`raisedCount`,
 * `oldestRaisedInstanceId`), not on any table row.
 */
export const eventQueueActionDefinitions: ActionDefinitions = [
  {
    id: 'clearNextEventInstance',
    label: 'Clear next (oldest)',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllEventQueue(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      if (only.raisedCount === 0 || only.oldestRaisedInstanceId === null) return 'disabled';
      const oldestId = only.oldestRaisedInstanceId;
      return () =>
        void store
          .dispatch(clearEventInstance({ event_instance_id: oldestId }))
          .unwrap()
          .then((res: { cleared_event_instance_ids?: number[] } | undefined) => {
            const clearedIds = res?.cleared_event_instance_ids ?? [];
            if (clearedIds.length > 0) toast.success(`Cleared event instance ${clearedIds.join(', ')}`);
            else toast.info('No raised event instance to clear.');
            refresh();
          })
          .catch((err: { error?: string } | undefined) => toast.error(err?.error || 'Failed to clear event instance.'));
    },
  },
  {
    id: 'clearAllEventInstances',
    label: 'Clear all raised',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllEventQueue(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      if (only.raisedCount === 0) return 'disabled';
      const { zoneName, name, raisedCount } = only;
      return () => {
        void (async () => {
          const confirmed = await confirmDialog({
            title: 'Clear all raised',
            body: `Clear all ${raisedCount} raised event instance${raisedCount === 1 ? '' : 's'} for "${name}"? This drains the entire queue and cannot be undone.`,
            confirmText: 'Clear all raised',
            confirmVariant: 'danger',
          });
          if (!confirmed) return;
          try {
            const res = (await store.dispatch(clearAllEventInstances({ zone_name: zoneName, name })).unwrap()) as
              | { cleared_event_instance_ids?: number[] }
              | undefined;
            const clearedIds = res?.cleared_event_instance_ids ?? [];
            // The bulk endpoint may not echo back cleared ids; fall back to the
            // count already shown to the operator in the confirmation dialog.
            const clearedCount = clearedIds.length > 0 ? clearedIds.length : raisedCount;
            toast.success(`Cleared ${clearedCount} raised event instance${clearedCount === 1 ? '' : 's'}.`);
            refresh();
          } catch (err) {
            toast.error((err as { error?: string })?.error || 'Failed to clear all event instances.');
          }
        })();
      };
    },
  },
];
